import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import adminService from '../../services/adminService';

const Settings = () => {
  const [settings, setSettings] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    supportPhone: '',
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    requirePhoneVerification: true,
    maxPhotosPerProfile: 5,
    membershipSettings: {
      freeTrial: true,
      freeTrialDays: 7,
      premiumPrice: 999,
      goldPrice: 1999,
      diamondPrice: 2999,
    },
    freePlanLimits: {
      sendInterestsPerDay: 20,
      superInterestsTotal: 0,
      shortlistProfilesTotal: 10,
      chatMessagesPerDay: 20,
    },
    adminAccount: {
      email: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminService.getSettings();
      const payload = response.data?.data || response.data || {};
      const adminAccount = response.data?.adminAccount || {};

      setSettings((prev) => ({
        ...prev,
        ...payload,
        membershipSettings: {
          ...prev.membershipSettings,
          ...(payload.membershipSettings || {}),
        },
        freePlanLimits: {
          ...prev.freePlanLimits,
          ...(payload.freePlanLimits || {}),
        },
        adminAccount: {
          ...prev.adminAccount,
          email: adminAccount.email || prev.adminAccount.email,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMembershipChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      membershipSettings: {
        ...prev.membershipSettings,
        [field]: value,
      },
    }));
  };

  const handleFreePlanLimitChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      freePlanLimits: {
        ...prev.freePlanLimits,
        [field]: value,
      },
    }));
  };

  const handleAdminAccountChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      adminAccount: {
        ...prev.adminAccount,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiresCredentialUpdate =
      settings.adminAccount.newPassword ||
      settings.adminAccount.currentPassword ||
      settings.adminAccount.confirmPassword;

    if (requiresCredentialUpdate) {
      if (!settings.adminAccount.currentPassword) {
        setError('Current password is required to update email or password');
        return;
      }
      if (
        settings.adminAccount.newPassword &&
        settings.adminAccount.newPassword !== settings.adminAccount.confirmPassword
      ) {
        setError('New password and confirm password do not match');
        return;
      }
    }

    try {
      setSaving(true);
      await adminService.updateSettings(settings);
      setSuccess(true);
      setSettings((prev) => ({
        ...prev,
        adminAccount: {
          ...prev.adminAccount,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
      }));
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        System Settings
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* General Settings */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Site Name"
                    value={settings.siteName}
                    onChange={(e) => handleChange('siteName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={settings.contactEmail}
                    onChange={(e) => handleChange('contactEmail', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Site Description"
                    value={settings.siteDescription}
                    onChange={(e) =>
                      handleChange('siteDescription', e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* System Configuration */}
          {/* <Grid item xs={12}> */}
            {/* <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                System Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.maintenanceMode}
                        onChange={(e) =>
                          handleChange('maintenanceMode', e.target.checked)
                        }
                      />
                    }
                    label="Maintenance Mode"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.allowRegistration}
                        onChange={(e) =>
                          handleChange('allowRegistration', e.target.checked)
                        }
                      />
                    }
                    label="Allow Registration"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.requireEmailVerification}
                        onChange={(e) =>
                          handleChange('requireEmailVerification', e.target.checked)
                        }
                      />
                    }
                    label="Require Email Verification"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.requirePhoneVerification}
                        onChange={(e) =>
                          handleChange('requirePhoneVerification', e.target.checked)
                        }
                      />
                    }
                    label="Require Phone Verification"
                  />
                </Grid>
              </Grid>
            </Paper> */}
          {/* </Grid> */}

          {/* Membership Settings */}
          {/* <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Membership Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.membershipSettings.freeTrial}
                        onChange={(e) =>
                          handleMembershipChange('freeTrial', e.target.checked)
                        }
                      />
                    }
                    label="Enable Free Trial"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Free Trial Days"
                    value={settings.membershipSettings.freeTrialDays}
                    onChange={(e) =>
                      handleMembershipChange('freeTrialDays', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Premium Price"
                    value={settings.membershipSettings.premiumPrice}
                    onChange={(e) =>
                      handleMembershipChange('premiumPrice', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Gold Price"
                    value={settings.membershipSettings.goldPrice}
                    onChange={(e) =>
                      handleMembershipChange('goldPrice', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Diamond Price"
                    value={settings.membershipSettings.diamondPrice}
                    onChange={(e) =>
                      handleMembershipChange('diamondPrice', e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid> */}

          {/* Admin Account Settings */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Admin Account Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Admin Email"
                    value={settings.adminAccount.email}
                    onChange={(e) => handleAdminAccountChange('email', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Current Password"
                    value={settings.adminAccount.currentPassword}
                    onChange={(e) => handleAdminAccountChange('currentPassword', e.target.value)}
                    helperText="Required to change email or password"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="New Password"
                    value={settings.adminAccount.newPassword}
                    onChange={(e) => handleAdminAccountChange('newPassword', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm New Password"
                    value={settings.adminAccount.confirmPassword}
                    onChange={(e) => handleAdminAccountChange('confirmPassword', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Free Plan Quick Limits */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Free Plan Quick Permission Limits
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set baseline limits for free users without editing paid plans.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Send Interests / Day"
                    value={settings.freePlanLimits.sendInterestsPerDay}
                    onChange={(e) => handleFreePlanLimitChange('sendInterestsPerDay', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Super Interests / Total"
                    value={settings.freePlanLimits.superInterestsTotal}
                    onChange={(e) => handleFreePlanLimitChange('superInterestsTotal', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Shortlists / Total"
                    value={settings.freePlanLimits.shortlistProfilesTotal}
                    onChange={(e) => handleFreePlanLimitChange('shortlistProfilesTotal', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Chat Messages / Day"
                    value={settings.freePlanLimits.chatMessagesPerDay}
                    onChange={(e) => handleFreePlanLimitChange('chatMessagesPerDay', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            size="large"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success">Settings updated successfully!</Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 